import React from 'react';
import { useFormContext } from 'react-hook-form';
import { Monitor, Clock, FileVideo, Volume2, Check, ShieldCheck, Sparkles, Loader2, AlertTriangle, Pencil, Plus } from 'lucide-react';
import type { CreativeSlotState, SelfServiceFormData } from '../../../types/selfService';
import { useReusableLibrary } from '../shared/useReusableLibrary';
import LibraryPickerModal, { type LibraryPick } from './LibraryPickerModal';
import ArchivedFileCollisionModal from '../shared/ArchivedFileCollisionModal';
import { StepHeader, accentVars, tintFromAccent } from './_shared';
import { useWaymark } from '../waymark/WaymarkProvider';
import { looksLikeWebsiteUrl } from '../waymark/normalizeUrl';
import { useAuth } from '../auth/useAuth';
import { apiClient, ApiError } from '../../../lib/apiClient';
import CreativeSlotCard from '../scheduler/CreativeSlotCard';
import MultiCreativeScheduler from '../scheduler/MultiCreativeScheduler';
import { buildStationPalette } from '../../../lib/dateAxisRendering';
import Banner from '../shared/Banner';
import PrimaryButton from '../shared/PrimaryButton';
import CoverageBanner from '../shared/CoverageBanner';
import ConcurrencyBanner from '../shared/ConcurrencyBanner';
import { validateCreativeCoverage, type UncoveredRange } from '../shared/creativeCoverage';
import {
  validateCreativeConcurrency,
  type OverCapRange,
} from '../shared/creativeConcurrency';

interface UploadCreativeStepProps {
  // The station slug — needed by /creatives/preview-url to resolve a signed
  // URL for a previously-uploaded storagePath without going through a
  // submission row.
  slug: string;
  accentColor?: string;
  primaryColor?: string;
  secondaryColor?: string;
  campaignStartDate: string;
  campaignEndDate: string;
  // Unified slot state. Slot 0 lives at slots[0]; all operations target a
  // specific slotId. The hook guarantees slots.length >= 1 so the step always
  // has something to render into.
  slots: CreativeSlotState[];
  files: Record<string, File | null>;
  uploadState: Record<string, { uploading: boolean; error: string | null }>;
  onFileChange: (slotId: string, file: File | null) => void;
  onSlotChange: (slotId: string, patch: Partial<CreativeSlotState>) => void;
  onSlotRemove: (slotId: string) => void;
  onAddSlot: () => void;
  // CTV-476: reset every overlap Rotation Segment to even split. Solo
  // segments stay 100% by construction. The scheduler also clears its
  // per-segment recency stack on reset.
  onResetWeights: () => void;
  // CTV-457 follow-up: the currently selected advertiser seat. When present,
  // and when the seat has reusable prior commercials, each slot gets a
  // "Pick from library" affordance so the user can skip the re-upload.
  advertiserId?: string | null;
  onPickFromLibrary?: (slotId: string, pick: LibraryPick) => void;
  // Waymark "Run this creative" wiring. `waymarkSlotKey` is the slot id where
  // the AI commercial currently lives; null means the user hasn't added it
  // yet (panel shows preview + buttons). The wizard's commit handler picks
  // slots[0].slotId for the "land in primary" path.
  waymarkSlotKey?: string | null;
  onCommitWaymark?: (blob: Blob, videoId: string) => void;
  onReplaceWaymark?: (blob: Blob, videoId: string) => void;
  // CTV-563: when the Waymark render fails our client-side ad-spec check
  // (duration / resolution), the wizard exposes the composed human message
  // here and the panel renders a rejection banner with a Try-again button.
  waymarkRejection?: string | null;
  onClearWaymarkRejection?: () => void;
  // Bubbles per-day coverage state up to the wizard so it can gate the
  // Continue button. Fires whenever the slot set or campaign window changes.
  onCoverageChange?: (uncoveredRanges: UncoveredRange[]) => void;
  // ADR-0008: bubbles per-day concurrency cap state up to the wizard so it
  // can gate Continue alongside coverage.
  onConcurrencyChange?: (overCapRanges: OverCapRange[]) => void;
}

const specs = [
  { icon: FileVideo, label: 'Format', value: 'MP4' },
  { icon: Clock, label: 'Duration', value: '15s or 30s' },
  { icon: Monitor, label: 'Resolution', value: '1920×1080 (1080p) min' },
  { icon: Volume2, label: 'Audio', value: 'Required' },
];

const UploadCreativeStep: React.FC<UploadCreativeStepProps> = ({
  slug,
  accentColor = '#3b82f6',
  primaryColor,
  secondaryColor,
  campaignStartDate,
  campaignEndDate,
  slots,
  files,
  uploadState,
  onFileChange,
  onSlotChange,
  onSlotRemove,
  onAddSlot,
  onResetWeights,
  advertiserId,
  onPickFromLibrary,
  waymarkSlotKey = null,
  onCommitWaymark,
  onReplaceWaymark,
  waymarkRejection = null,
  onClearWaymarkRejection,
  onCoverageChange,
  onConcurrencyChange,
}) => {
  // CTV-457 follow-up: drives "Pick from library" visibility and feeds the
  // modal. Filters out voided-only items. The button is only rendered when
  // there's at least one reusable commercial — first-time advertisers see
  // the upload-only UI as before.
  const { hasReusable, archived: archivedLibrary } = useReusableLibrary(
    slug,
    advertiserId ?? null,
  );
  const [pickerTarget, setPickerTarget] = React.useState<string | null>(null);
  const canPick = hasReusable && !!advertiserId;
  const handlePickerPick = (pick: LibraryPick) => {
    if (pickerTarget) onPickFromLibrary?.(pickerTarget, pick);
    setPickerTarget(null);
  };

  // CTV-526: upload-collision prompt. When a fresh File pick at a slot
  // matches an archived tile by (filename, size_bytes), divert into the
  // modal before the upload starts. Both modal branches end with the
  // archive removed so the file is back in the active library.
  const [collision, setCollision] = React.useState<null | {
    slotId: string;
    file: File;
    archivedStoragePath: string;
    archivedMimeType: string | null;
  }>(null);
  const handleFileChangeWithCollisionCheck = React.useCallback(
    (slotId: string, file: File | null) => {
      if (!file || !advertiserId) {
        onFileChange(slotId, file);
        return;
      }
      const match = archivedLibrary.find(
        (a) => a.filename === file.name && (a.size_bytes ?? 0) === file.size,
      );
      if (!match) {
        onFileChange(slotId, file);
        return;
      }
      setCollision({
        slotId,
        file,
        archivedStoragePath: match.preview_storage_path,
        archivedMimeType: match.mime_type,
      });
    },
    [advertiserId, archivedLibrary, onFileChange],
  );

  // Waymark commit/replace bridge — replaces the prior auto-import effect.
  // The user explicitly commits via "Run this creative" (handleRunWaymark
  // below); after that, any new renderedUrl (i.e. the user re-customized via
  // the editor) is fetched and routed through `onReplaceWaymark` so the slot
  // updates in place rather than appending another copy.
  const { renderedUrl, videoId, openEditor } = useWaymark();
  const [isCommitting, setIsCommitting] = React.useState(false);
  const committedRenderedUrlRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (waymarkSlotKey === null) {
      committedRenderedUrlRef.current = null;
    }
  }, [waymarkSlotKey]);

  React.useEffect(() => {
    if (!renderedUrl || !videoId) return;
    if (waymarkSlotKey === null) return;
    if (renderedUrl === committedRenderedUrlRef.current) return;
    if (!onReplaceWaymark) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(renderedUrl);
        if (!res.ok) {
          console.warn('[waymark] replace: fetch returned', res.status);
          return;
        }
        const blob = await res.blob();
        if (cancelled) return;
        onReplaceWaymark(blob, videoId);
        committedRenderedUrlRef.current = renderedUrl;
      } catch (e) {
        console.warn('[waymark] replace failed', e);
      }
    })();
    return () => { cancelled = true; };
  }, [renderedUrl, videoId, waymarkSlotKey, onReplaceWaymark]);

  const handleRunWaymark = React.useCallback(async () => {
    if (!renderedUrl || !videoId) return;
    if (!onCommitWaymark) return;
    setIsCommitting(true);
    try {
      const res = await fetch(renderedUrl);
      if (!res.ok) {
        console.warn('[waymark] commit: fetch returned', res.status);
        return;
      }
      const blob = await res.blob();
      onCommitWaymark(blob, videoId);
      committedRenderedUrlRef.current = renderedUrl;
    } catch (e) {
      console.warn('[waymark] commit failed', e);
    } finally {
      setIsCommitting(false);
    }
  }, [renderedUrl, videoId, onCommitWaymark]);

  // A slot "exists" for the scheduler only once it has a creative in it.
  // Empty slots remain rendered as upload cards (so the user has somewhere to
  // put the next video) but are excluded from the rotation/gantt and don't
  // count toward weight rebalancing.
  const slotHasContent = (s: CreativeSlotState) => !!s.storagePath || !!s.filename;
  const visibleSlots = React.useMemo<CreativeSlotState[]>(
    () => slots.filter(slotHasContent),
    [slots],
  );
  const showScheduler = visibleSlots.length >= 2;
  const slot0HasContent = !!slots[0] && slotHasContent(slots[0]);

  // CTV-457: positional palette anchored on the station's brand colours.
  // The same array drives the colour dot on every slot card AND the gantt
  // bar in the Scheduler — they were drifting because each surface was
  // building its own palette. Build once here and pass down. Palette length
  // tracks visibleSlots so newly-committed slots pick up the next colour.
  const paletteSize = Math.max(visibleSlots.length, 6);
  const palette = React.useMemo(
    () => buildStationPalette(primaryColor, secondaryColor, paletteSize),
    [primaryColor, secondaryColor, paletteSize],
  );
  const slotColors = React.useMemo(
    () => Array.from({ length: visibleSlots.length }, (_, i) => palette[i % palette.length]),
    [palette, visibleSlots.length],
  );
  const visiblePosition = React.useCallback(
    (slotId: string) => visibleSlots.findIndex((s) => s.slotId === slotId),
    [visibleSlots],
  );

  const hasMultipleSlots = slots.length > 1;

  // Per-day creative coverage (ADR-0006). Computed from visibleSlots so empty
  // slots and pending-but-not-yet-uploaded slots don't count.
  const coverage = React.useMemo(
    () =>
      validateCreativeCoverage({
        windowStart: campaignStartDate,
        windowEnd: campaignEndDate,
        creatives: visibleSlots.map((s) => ({ startDate: s.startDate, endDate: s.endDate })),
      }),
    [campaignStartDate, campaignEndDate, visibleSlots],
  );
  React.useEffect(() => {
    onCoverageChange?.(coverage.uncoveredRanges);
  }, [coverage, onCoverageChange]);

  // ADR-0008: per-day concurrency cap. Same input set as coverage; lifted
  // separately so the wizard's Continue gate can require both.
  const concurrency = React.useMemo(
    () =>
      validateCreativeConcurrency({
        windowStart: campaignStartDate,
        windowEnd: campaignEndDate,
        creatives: visibleSlots.map((s) => ({ startDate: s.startDate, endDate: s.endDate })),
      }),
    [campaignStartDate, campaignEndDate, visibleSlots],
  );
  React.useEffect(() => {
    onConcurrencyChange?.(concurrency.overCapRanges);
  }, [concurrency, onConcurrencyChange]);

  return (
    <div className="-m-4 sm:-m-6 md:-m-8" style={accentVars(accentColor, primaryColor)}>
      <div className="px-6 sm:px-10 md:px-14 pt-6 pb-6">
        <StepHeader step={3} label="Commercial" accentColor={accentColor} />
      </div>

      <div className="px-6 sm:px-10 md:px-14 pb-10 space-y-6">
        {/* CTV-558: Upload → Waymark → Specs (was Specs → Waymark → Upload).
            Buyers should land on the upload affordance first; specs are a
            tail reference. */}

        {/* Single render loop — every slot, including slot 0, goes through
            CreativeSlotCard. With only one slot the card has no header chrome
            (no badge, no remove, no weight), so single-creative UX matches
            the legacy "primary uploader" surface. */}
        <div className="space-y-5">
          {slots.map((slot) => {
            const pos = visiblePosition(slot.slotId);
            const color = pos >= 0 ? slotColors[pos] : '#cbd5e1';
            return (
              <CreativeSlotCard
                key={slot.slotId}
                slot={slot}
                slug={slug}
                file={files[slot.slotId] ?? null}
                accentColor={accentColor}
                color={color}
                index={pos >= 0 ? pos : -1}
                removable={hasMultipleSlots}
                uploading={uploadState[slot.slotId]?.uploading ?? false}
                uploadError={uploadState[slot.slotId]?.error ?? null}
                onFileChange={(file) =>
                  handleFileChangeWithCollisionCheck(slot.slotId, file)
                }
                onClickThroughUrlChange={(next) => onSlotChange(slot.slotId, { clickThroughUrl: next })}
                onRemove={() => onSlotRemove(slot.slotId)}
                onPickFromLibrary={canPick ? () => setPickerTarget(slot.slotId) : undefined}
                onCustomize={waymarkSlotKey === slot.slotId ? () => void openEditor() : undefined}
              />
            );
          })}
        </div>

        {/* "+ Add another commercial" — gated on slot 0 having content so the
            user can't stack empty upload cards. */}
        {slot0HasContent && (
          <button
            type="button"
            onClick={onAddSlot}
            className="inline-flex items-center gap-2 text-[12px] font-semibold tracking-tight text-slate-600 hover:text-slate-900 bg-white border border-dashed border-slate-300 hover:border-slate-500 rounded-full px-4 py-2 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Add another commercial
          </button>
        )}

        <WaymarkPanel
          accentColor={accentColor}
          primaryColor={primaryColor}
          waymarkCommitted={waymarkSlotKey !== null}
          onRunWaymark={handleRunWaymark}
          isCommitting={isCommitting}
          rejection={waymarkRejection}
          onClearRejection={onClearWaymarkRejection}
        />

        {/* CTV-558: Ad specifications — one compact reference line instead
            of the prior two-column card; sits below upload + Waymark so the
            buyer's primary action lands at the top. */}
        <div
          className="rounded-2xl border border-slate-200/80 bg-white overflow-hidden"
          style={{ boxShadow: 'var(--card-shadow)' }}
        >
          <div className="px-5 py-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
            <span className="text-[10px] font-medium uppercase tracking-[0.08em] text-slate-400 mr-1">
              Ad specs
            </span>
            {specs.map(({ label, value }, i) => (
              <React.Fragment key={label}>
                {i > 0 && <span aria-hidden className="text-slate-300">·</span>}
                <span>
                  <span className="text-slate-400">{label}: </span>
                  <span className="text-slate-700 font-medium">{value}</span>
                </span>
              </React.Fragment>
            ))}
          </div>
          <div
            className="px-5 py-2.5 border-t border-slate-200/70 flex items-start gap-2"
            style={{ backgroundColor: tintFromAccent(accentColor, 0.04) }}
          >
            <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: accentColor }} />
            <p className="text-[11px] text-slate-600 leading-relaxed">
              All commercials are reviewed before launch, typically within 24–48 hours.
            </p>
          </div>
        </div>

        {/* Rotation & schedule — only rendered for multi-commercial campaigns;
            sits at the bottom of the step so the upload affordance and
            ad-spec reference both land before the buyer thinks about
            scheduling. */}
        {showScheduler && (
          <MultiCreativeScheduler
            slots={visibleSlots}
            colors={slotColors}
            campaignStartDate={campaignStartDate}
            campaignEndDate={campaignEndDate}
            accentColor={accentColor}
            slug={slug}
            onSlotChange={onSlotChange}
            onResetWeights={onResetWeights}
          />
        )}

        {visibleSlots.length > 0 && coverage.uncoveredRanges.length > 0 && (
          <CoverageBanner
            uncoveredRanges={coverage.uncoveredRanges}
            surface="wizard"
            slots={visibleSlots.map((s) => ({
              id: s.slotId,
              filename: s.filename,
              startDate: s.startDate,
              endDate: s.endDate,
            }))}
            onExtendLeftCreative={(id, newEnd) => onSlotChange(id, { endDate: newEnd })}
            onExtendRightCreative={(id, newStart) => onSlotChange(id, { startDate: newStart })}
          />
        )}
        {visibleSlots.length > 0 && concurrency.overCapRanges.length > 0 && (
          <ConcurrencyBanner overCapRanges={concurrency.overCapRanges} />
        )}
      </div>

      {advertiserId && (
        <LibraryPickerModal
          open={pickerTarget !== null}
          slug={slug}
          advertiserId={advertiserId}
          onClose={() => setPickerTarget(null)}
          onPick={handlePickerPick}
        />
      )}

      {collision && advertiserId && (
        <ArchivedFileCollisionModal
          slug={slug}
          advertiserId={advertiserId}
          filename={collision.file.name}
          sizeBytes={collision.file.size}
          archivedStoragePath={collision.archivedStoragePath}
          archivedMimeType={collision.archivedMimeType}
          accentColor={accentColor}
          onClose={() => setCollision(null)}
          onReuseArchived={(info) => {
            // Skip the upload entirely: feed the archived storage path to the
            // slot via the LibraryPick path, exactly like a Library Picker
            // selection. The wizard's onPickFromLibrary collapses to
            // applyLibraryPick under the hood.
            if (onPickFromLibrary) {
              onPickFromLibrary(collision.slotId, {
                storagePath: info.storagePath,
                filename: info.filename,
                mimeType: info.mimeType,
                sizeBytes: info.sizeBytes,
                clickThroughUrl: '',
              });
            }
            setCollision(null);
          }}
          onUploadAnyway={() => {
            onFileChange(collision.slotId, collision.file);
            setCollision(null);
          }}
        />
      )}
    </div>
  );
};

interface WaymarkPanelProps {
  accentColor: string;
  primaryColor?: string;
  // True once the user has committed this Waymark ad to any slot. Drives the
  // "preview + buttons" vs "added to campaign" branches below.
  waymarkCommitted: boolean;
  // Wired to UploadCreativeStep's handleRunWaymark — fetches the rendered
  // blob and routes it through the wizard's commit handler.
  onRunWaymark: () => void | Promise<void>;
  // True while handleRunWaymark is fetching the blob.
  isCommitting: boolean;
  // CTV-563: composed human message when the last Waymark render failed the
  // ad-spec check. Null when there's no active rejection. Clearing it lets
  // the buyer try again.
  rejection?: string | null;
  onClearRejection?: () => void;
}

const WaymarkPanel: React.FC<WaymarkPanelProps> = ({ accentColor, primaryColor, waymarkCommitted, onRunWaymark, isCommitting, rejection = null, onClearRejection }) => {
  const { watch, setValue } = useFormContext<SelfServiceFormData>();
  const businessWebsite = (watch('businessWebsite') ?? '').trim();
  const { status, sourceUrl, renderedUrl, openEditor, startGenerationForUrl, isHydrating, isFinalizing } = useWaymark();
  // CTV-562 follow-up: the Waymark source-URL editor is a second surface
  // that mutates business identity. Route the change through the seat's
  // PATCH /advertisers/:id + refreshMemberships so the read-only summary
  // on Step 1 reflects the new URL (single source of truth). Without this,
  // the multi-seat dropdown summary reads selected.businessWebsite from
  // the stale memberships list and Step 1 keeps showing the old URL.
  const { selectedAdvertiser, refreshMemberships } = useAuth();

  // CTV-558: gate URL swaps during in-flight generation behind a confirm
  // modal. `pendingUrl` is the candidate URL the user just typed; the
  // resolver is the promise hook that WaymarkSourceWebsiteRow is awaiting,
  // so we can answer it after the modal closes.
  const [confirmTarget, setConfirmTarget] = React.useState<{
    url: string;
    resolve: (ok: boolean) => void;
  } | null>(null);

  // CTV-564: latch true once isFinalizing has fired in this generation
  // cycle. Without this, the bar reverts to "You customize" after the
  // broadcast render finishes (because isFinalizing flips back to false
  // and status stays 'ready'). The provider doesn't persist this — it's
  // an in-session signal, reset whenever a new generation begins.
  const [hasFinalized, setHasFinalized] = React.useState(false);
  React.useEffect(() => {
    if (isFinalizing) setHasFinalized(true);
  }, [isFinalizing]);
  React.useEffect(() => {
    if (status !== 'ready') setHasFinalized(false);
  }, [status]);

  // CTV-562 follow-up: PATCH /advertisers/:id + refreshMemberships when the
  // Waymark source URL commit diverges from the seat's stored website. Throws
  // on failure so the inline SourceUrlEditor error surface (which now catches
  // onBeforeCommit throws) can display a message and keep the user in edit
  // mode. No-op when there's no seat (anonymous flow) or when the value
  // hasn't actually changed against the seat.
  const commitSeatWebsite = React.useCallback(
    async (next: string): Promise<void> => {
      if (!selectedAdvertiser) return;
      const trimmed = next.trim();
      const current = (selectedAdvertiser.businessWebsite ?? '').trim();
      if (trimmed === current) return;
      try {
        await apiClient.patch(`/advertisers/${selectedAdvertiser.advertiserId}`, {
          businessWebsite: trimmed || null,
        });
        await refreshMemberships();
      } catch (e) {
        throw e instanceof ApiError
          ? e
          : new Error(e instanceof Error ? e.message : 'Failed to update business URL.');
      }
    },
    [selectedAdvertiser, refreshMemberships],
  );

  const handleSourceCommit = React.useCallback(
    async (next: string): Promise<boolean> => {
      // Failed → swap and immediately re-attempt with the new URL; no
      // confirm needed since the prior run is already dead.
      if (status === 'topic_failed' || status === 'generation_failed') {
        await commitSeatWebsite(next);
        startGenerationForUrl(next);
        return true;
      }
      // Idle → just update the value. The "Build my commercial" button
      // remains the explicit start trigger.
      if (status === 'idle') {
        await commitSeatWebsite(next);
        return true;
      }
      // In-flight (topic_searching / generating / ready) → confirm before
      // tearing down the running generation.
      const ok = await new Promise<boolean>((resolve) => {
        setConfirmTarget({ url: next, resolve });
      });
      if (!ok) return false;
      await commitSeatWebsite(next);
      startGenerationForUrl(next);
      return true;
    },
    [status, startGenerationForUrl, commitSeatWebsite],
  );

  if (isHydrating) return null;

  // CTV-563: rejection banner sits above every other panel state — once a
  // Waymark render has been flagged as out-of-spec we hold this callout in
  // front of the buyer until they either retry generation or edit the
  // commercial. Try again clears the rejection and re-kicks Waymark.
  if (rejection) {
    return (
      <Banner
        tone="warn"
        label="This commercial doesn't meet the ad specs"
        icon={<AlertTriangle className="w-3 h-3" />}
      >
        <p>{rejection}</p>
        <button
          type="button"
          onClick={() => {
            onClearRejection?.();
            if (businessWebsite) startGenerationForUrl(businessWebsite);
          }}
          className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-medium text-rose-900 hover:text-rose-950 underline underline-offset-2"
        >
          Try again
        </button>
      </Banner>
    );
  }

  if (!businessWebsite && status === 'idle') {
    return (
      <WaymarkWebsiteCapture
        accentColor={accentColor}
        primaryColor={primaryColor}
        onCommit={(url) => {
          setValue('businessWebsite', url, { shouldDirty: true });
          startGenerationForUrl(url);
        }}
      />
    );
  }

  if (status === 'topic_failed' || status === 'generation_failed') {
    return (
      <Banner
        tone="warn"
        label="We couldn't build your commercial"
        icon={<AlertTriangle className="w-3 h-3" />}
      >
        <p>
          Try a different website, or upload your own video above.
        </p>
        <WaymarkSourceWebsiteRow
          accentColor={accentColor}
          primaryColor={primaryColor}
          value={businessWebsite || (sourceUrl ?? '')}
          onChange={(next) => setValue('businessWebsite', next, { shouldDirty: true })}
          onBeforeCommit={handleSourceCommit}
        />
        <button
          type="button"
          onClick={() => businessWebsite && startGenerationForUrl(businessWebsite)}
          className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-medium text-rose-900 hover:text-rose-950 underline underline-offset-2"
        >
          Try again
        </button>
      </Banner>
    );
  }

  // Once the user has run the creative AND the broadcast-quality render
  // is done, the slot itself is enough signal — hide the WaymarkPanel so
  // there's no "Run this creative" button lingering for a commercial the
  // user has already committed.
  if (status === 'ready' && waymarkCommitted && !isFinalizing) {
    return null;
  }

  // Stages 0–3 share one persistent banner. activeIndex tracks the
  // currently-running stage; allComplete short-circuits the "all done,
  // waiting on Run" terminal state where every dot shows a check.
  if (
    status === 'topic_searching' ||
    status === 'generating' ||
    status === 'ready'
  ) {
    const allComplete = status === 'ready' && !isFinalizing && hasFinalized;
    const activeIndex: 0 | 1 | 2 | 3 =
      status === 'topic_searching'
        ? 0
        : status === 'generating'
          ? 1
          : isFinalizing
            ? 3
            : 2;

    return (
      <>
        <div
          className="rounded-2xl border overflow-hidden"
          style={{
            borderColor: tintFromAccent(accentColor, 0.35),
            background: `linear-gradient(180deg, ${tintFromAccent(accentColor, 0.06)} 0%, ${tintFromAccent(accentColor, 0.02)} 100%)`,
          }}
        >
          <div className="px-5 pt-5 pb-3">
            <div className="text-[16px] font-semibold tracking-tight text-slate-900">
              Creating your commercial
            </div>
            <WaymarkSourceWebsiteRow
              accentColor={accentColor}
              primaryColor={primaryColor}
              value={businessWebsite}
              onChange={(next) => setValue('businessWebsite', next, { shouldDirty: true })}
              onBeforeCommit={handleSourceCommit}
            />
          </div>

          <div className="px-5 pb-4">
            <GenerationProgressStrip
              activeIndex={activeIndex}
              allComplete={allComplete}
              accentColor={accentColor}
            />
          </div>

          <div className="px-5 pb-5">
            {!allComplete && activeIndex === 0 && (
              <StageSpinnerRow text="Analyzing your website…" accentColor={accentColor} />
            )}
            {!allComplete && activeIndex === 1 && (
              <StageSpinnerRow text="Drafting your commercial…" accentColor={accentColor} />
            )}
            {!allComplete && activeIndex === 2 && (
              <CustomizeStageBody
                mode="customize-required"
                renderedUrl={renderedUrl}
                onCustomize={() => void openEditor()}
                onRunWaymark={onRunWaymark}
                isCommitting={isCommitting}
                accentColor={accentColor}
                primaryColor={primaryColor}
              />
            )}
            {!allComplete && activeIndex === 3 && (
              <StageSpinnerRow
                text="Final render in progress — about 3 minutes total."
                accentColor={accentColor}
              />
            )}
            {allComplete && (
              <CustomizeStageBody
                mode="ready-to-run"
                renderedUrl={renderedUrl}
                onCustomize={() => void openEditor()}
                onRunWaymark={onRunWaymark}
                isCommitting={isCommitting}
                accentColor={accentColor}
                primaryColor={primaryColor}
              />
            )}
          </div>
        </div>

        {confirmTarget && (
          <RestartGenerationConfirm
            accentColor={accentColor}
            primaryColor={primaryColor}
            newUrl={confirmTarget.url}
            onCancel={() => {
              confirmTarget.resolve(false);
              setConfirmTarget(null);
            }}
            onConfirm={() => {
              confirmTarget.resolve(true);
              setConfirmTarget(null);
            }}
          />
        )}
      </>
    );
  }

  // Idle but website-on-file — pre-generation "Build my commercial" card.
  return (
    <div
      className="rounded-2xl border border-dashed overflow-hidden"
      style={{
        borderColor: tintFromAccent(accentColor, 0.35),
        background: `linear-gradient(180deg, ${tintFromAccent(accentColor, 0.06)} 0%, ${tintFromAccent(accentColor, 0.02)} 100%)`,
      }}
    >
      <div className="px-5 py-5 flex items-start gap-3">
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
          style={{ backgroundColor: tintFromAccent(accentColor, 0.14), color: accentColor }}
        >
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div
            className="text-[10px] font-semibold uppercase tracking-[0.14em] mb-1"
            style={{ color: accentColor }}
          >
            Don't have a commercial?
          </div>
          <div className="text-[15px] font-semibold tracking-tight text-slate-900">
            Build one from your website
          </div>
          <WaymarkSourceWebsiteRow
            accentColor={accentColor}
            primaryColor={primaryColor}
            value={businessWebsite}
            onChange={(next) => setValue('businessWebsite', next, { shouldDirty: true })}
            onBeforeCommit={handleSourceCommit}
          />
          <p className="text-[12px] text-slate-500 leading-relaxed mt-2">
            We'll draft a 30-second commercial you can edit. Takes about five minutes total.
          </p>
          <div className="mt-3">
            <PrimaryButton
              type="button"
              onClick={() => startGenerationForUrl(businessWebsite)}
              accentColor={accentColor}
              primaryColor={primaryColor}
              size="sm"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Build my commercial
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
};

// CTV-558 / CTV-564: 4-stage horizontal stepper covering the Waymark
// pipeline the user actually walks through — analyze → draft → customize
// (required) → final render. The 4-stage layout reads more clearly than
// a collapsed 3-stage version; the post-finalize "all complete" state is
// driven by an `allComplete` prop that renders every dot as a check.
const GENERATION_STAGES = [
  { label: 'Analyzing', hint: '~30s', kind: 'wait' as const },
  { label: 'Drafting', hint: '~4 min', kind: 'wait' as const },
  { label: 'You customize', hint: '', kind: 'action' as const },
  { label: 'Final render', hint: '~3 min', kind: 'wait' as const },
] as const;

const GenerationProgressStrip: React.FC<{
  activeIndex: 0 | 1 | 2 | 3;
  allComplete: boolean;
  accentColor: string;
}> = ({ activeIndex, allComplete, accentColor }) => {
  return (
    <div>
      {/* Dots + connectors */}
      <div className="flex items-center">
        {GENERATION_STAGES.map((stage, i) => {
          const isComplete = allComplete || i < activeIndex;
          const isActive = !allComplete && i === activeIndex;
          return (
            <React.Fragment key={i}>
              <div
                className="flex items-center justify-center w-6 h-6 rounded-full shrink-0"
                style={{
                  backgroundColor: isComplete
                    ? accentColor
                    : isActive
                      ? tintFromAccent(accentColor, 0.18)
                      : '#f1f5f9',
                  border: isActive ? `2px solid ${accentColor}` : '2px solid transparent',
                  color: isComplete ? '#ffffff' : accentColor,
                }}
              >
                {isComplete ? (
                  <Check className="w-3.5 h-3.5" strokeWidth={3} />
                ) : isActive ? (
                  stage.kind === 'action' ? (
                    <Pencil className="w-3 h-3" />
                  ) : (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  )
                ) : (
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ backgroundColor: '#cbd5e1' }}
                  />
                )}
              </div>
              {i < GENERATION_STAGES.length - 1 && (() => {
                const connectorComplete = allComplete || i < activeIndex;
                return (
                  <div
                    className="flex-1 h-[2px] mx-1.5"
                    style={{
                      backgroundColor: connectorComplete
                        ? accentColor
                        : tintFromAccent(accentColor, 0.18),
                      backgroundImage: connectorComplete
                        ? undefined
                        : 'repeating-linear-gradient(90deg, currentColor 0 3px, transparent 3px 6px)',
                      color: connectorComplete ? undefined : '#cbd5e1',
                    }}
                  />
                );
              })()}
            </React.Fragment>
          );
        })}
      </div>

      {/* Labels under each dot */}
      <div className="flex items-start mt-1.5">
        {GENERATION_STAGES.map((stage, i) => {
          const isActive = !allComplete && i === activeIndex;
          const isComplete = allComplete || i < activeIndex;
          return (
            <React.Fragment key={i}>
              <div className="w-6 shrink-0 flex flex-col items-center">
                <div
                  className="text-[10px] font-semibold tracking-tight whitespace-nowrap"
                  style={{
                    color: isActive
                      ? accentColor
                      : isComplete
                        ? '#475569'
                        : '#94a3b8',
                  }}
                >
                  {stage.label}
                </div>
                {stage.hint && (
                  <div
                    className="text-[9px] font-medium uppercase tracking-[0.06em]"
                    style={{ color: isActive ? accentColor : '#94a3b8' }}
                  >
                    {stage.hint}
                  </div>
                )}
              </div>
              {i < GENERATION_STAGES.length - 1 && <div className="flex-1" />}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

// CTV-558: compact in-banner spinner row used for wait stages (Analyzing,
// Drafting, Final render). One line, lower-case, no second paragraph.
const StageSpinnerRow: React.FC<{ text: string; accentColor: string }> = ({
  text,
  accentColor,
}) => (
  <div
    className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg"
    style={{
      backgroundColor: tintFromAccent(accentColor, 0.08),
      border: `1px solid ${tintFromAccent(accentColor, 0.16)}`,
    }}
  >
    <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" style={{ color: accentColor }} />
    <span className="text-[13px] font-medium text-slate-700">{text}</span>
  </div>
);

// CTV-558 / CTV-564: stage-3+ body. Two modes:
//   - 'customize-required': draft preview + Customize button only. Run is
//     hidden because the user must customize before shipping.
//   - 'ready-to-run': post-finalize preview + Customize (secondary) +
//     Run this creative (primary).
const CustomizeStageBody: React.FC<{
  mode: 'customize-required' | 'ready-to-run';
  renderedUrl: string | null;
  onCustomize: () => void;
  onRunWaymark: () => void | Promise<void>;
  isCommitting: boolean;
  accentColor: string;
  primaryColor?: string;
}> = ({ mode, renderedUrl, onCustomize, onRunWaymark, isCommitting, accentColor, primaryColor }) => (
  <div className="space-y-3">
    {renderedUrl ? (
      <video
        src={renderedUrl}
        controls
        className="w-full rounded-lg border border-slate-200/70 bg-black"
      />
    ) : (
      <div
        className="rounded-lg px-3.5 py-2.5 text-[12px] text-slate-700"
        style={{
          backgroundColor: tintFromAccent(accentColor, 0.06),
          border: `1px solid ${tintFromAccent(accentColor, 0.16)}`,
        }}
      >
        Click <span className="font-semibold">Customize</span> to preview and finish your
        commercial.
      </div>
    )}
    {mode === 'customize-required' && (
      <p className="text-center text-[12px] text-slate-600">
        Click <span className="font-semibold">Customize</span> to finish your commercial.
      </p>
    )}
    <div className="flex items-center justify-center gap-2 flex-wrap">
      {/* Customize is the primary CTA before the user has previewed the
          draft (customize-required). Once the render is done and Run this
          commercial is the primary action, Customize demotes to a neutral
          slate pill so it doesn't compete visually. */}
      {mode === 'customize-required' ? (
        <PrimaryButton
          type="button"
          onClick={onCustomize}
          accentColor={accentColor}
          primaryColor={primaryColor}
          size="sm"
        >
          <Pencil className="w-3.5 h-3.5" />
          Customize
        </PrimaryButton>
      ) : (
        <button
          type="button"
          onClick={onCustomize}
          className="inline-flex items-center justify-center gap-1.5 px-4 py-1.5 text-[12px] font-semibold tracking-tight text-slate-700 bg-white hover:bg-slate-50 border border-slate-200 rounded-full transition-colors"
        >
          <Pencil className="w-3.5 h-3.5" />
          Customize
        </button>
      )}
      {mode === 'ready-to-run' && renderedUrl && (
        <PrimaryButton
          type="button"
          onClick={() => void onRunWaymark()}
          disabled={isCommitting}
          accentColor={accentColor}
          primaryColor={primaryColor}
          size="sm"
        >
          {isCommitting ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Sparkles className="w-3.5 h-3.5" />
          )}
          Run this commercial
        </PrimaryButton>
      )}
    </div>
  </div>
);

// CTV-558: plain confirm modal shown when the user edits the source URL
// during topic_searching / generating / ready. Modeled on the
// CancelCampaignModal shell (overlay + accent rail card + two buttons) but
// inlined and minimal — no type-to-confirm gate since the action is fully
// reversible.
const RestartGenerationConfirm: React.FC<{
  accentColor: string;
  primaryColor?: string;
  newUrl: string;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ accentColor, primaryColor, newUrl, onCancel, onConfirm }) => (
  <>
    <div
      className="fixed inset-0 bg-slate-900/45 backdrop-blur-[2px] z-50"
      onClick={(e) => {
        e.stopPropagation();
        onCancel();
      }}
    />
    <div
      className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90%] max-w-[420px] bg-white rounded-2xl border border-slate-200/80 overflow-hidden"
      style={{ boxShadow: '0 24px 48px -16px rgba(15,23,42,0.28), 0 2px 4px -1px rgba(15,23,42,0.05)' }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="h-[3px]"
        style={{ background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)` }}
      />
      <div className="p-5">
        <div className="text-[15px] font-semibold tracking-tight text-slate-900">
          Restart generation?
        </div>
        <p className="text-[13px] text-slate-600 mt-1.5 leading-relaxed">
          We'll rebuild your commercial from{' '}
          <span className="font-medium text-slate-800">{newUrl}</span>. This takes about
          5 minutes total.
        </p>
        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="text-[12px] font-medium text-slate-600 hover:text-slate-900 px-3 py-2 rounded-full transition-colors"
          >
            Cancel
          </button>
          <PrimaryButton
            type="button"
            onClick={onConfirm}
            accentColor={accentColor}
            primaryColor={primaryColor}
            size="sm"
          >
            <Sparkles className="w-3.5 h-3.5" />
            Restart
          </PrimaryButton>
        </div>
      </div>
    </div>
  </>
);

// CTV-558: inline website input shown on the Waymark idle panel when the
// buyer hasn't supplied a businessWebsite yet. Lets them seed Waymark
// without bouncing back to Step 1.
const WaymarkWebsiteCapture: React.FC<{
  accentColor: string;
  primaryColor?: string;
  onCommit: (url: string) => void;
}> = ({ accentColor, primaryColor, onCommit }) => {
  const [draft, setDraft] = React.useState('');
  const trimmed = draft.trim();
  return (
    <div
      className="rounded-2xl border border-dashed overflow-hidden"
      style={{
        borderColor: tintFromAccent(accentColor, 0.35),
        background: `linear-gradient(180deg, ${tintFromAccent(accentColor, 0.06)} 0%, ${tintFromAccent(accentColor, 0.02)} 100%)`,
      }}
    >
      <div className="px-5 py-5 flex items-start gap-3">
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
          style={{ backgroundColor: tintFromAccent(accentColor, 0.14), color: accentColor }}
        >
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div
            className="text-[10px] font-semibold uppercase tracking-[0.14em] mb-1"
            style={{ color: accentColor }}
          >
            Don't have a commercial?
          </div>
          <div className="text-[15px] font-semibold tracking-tight text-slate-900">
            Build one from your website
          </div>
          <p className="text-[12px] text-slate-500 leading-relaxed mt-1">
            Paste your business website and we'll draft a 30-second commercial you can edit.
          </p>
          <div className="mt-3 flex gap-2">
            <input
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && trimmed) {
                  e.preventDefault();
                  onCommit(trimmed);
                }
              }}
              placeholder="yourbusiness.com"
              className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-offset-1"
              style={{ ['--tw-ring-color' as string]: accentColor }}
            />
            <PrimaryButton
              type="button"
              onClick={() => trimmed && onCommit(trimmed)}
              disabled={!trimmed}
              accentColor={accentColor}
              primaryColor={primaryColor}
              size="sm"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Build my commercial
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
};

// CTV-558 / CTV-564: tiny "current: <url>" + edit pencil row used in both
// the Waymark idle panel and the in-flight progress banner. Edits flip to
// an inline input with explicit Save / Cancel buttons. `onBeforeCommit`
// lets the parent gate the change (e.g., to confirm "restart generation?"
// during topic_searching/generating/ready). The editor dismisses after
// onBeforeCommit resolves either way — true commits the new URL, false
// rolls back to the prior value.
const WaymarkSourceWebsiteRow: React.FC<{
  accentColor: string;
  primaryColor?: string;
  value: string;
  onChange: (next: string) => void;
  onBeforeCommit?: (next: string) => Promise<boolean>;
}> = ({ accentColor, primaryColor, value, onChange, onBeforeCommit }) => {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(value);
  const [error, setError] = React.useState<string | null>(null);
  const committingRef = React.useRef(false);

  React.useEffect(() => {
    if (!editing) {
      setDraft(value);
      setError(null);
    }
  }, [editing, value]);

  const cancel = () => {
    if (committingRef.current) return;
    setEditing(false);
  };

  const save = async () => {
    if (committingRef.current) return;
    const next = draft.trim();
    if (!next || next === value) {
      setEditing(false);
      return;
    }
    // CTV-564: reject obvious garbage before invoking the modal /
    // restarting Waymark. Real-but-dead domains still fall through to the
    // existing topic_failed banner; this only catches non-URL inputs.
    if (!looksLikeWebsiteUrl(next)) {
      setError("That doesn't look like a website URL.");
      return;
    }
    if (onBeforeCommit) {
      committingRef.current = true;
      try {
        // CTV-562 follow-up: onBeforeCommit may throw when a side-effect
        // (like a seat PATCH) fails. Catch here so the user sees the
        // failure inline and stays in edit mode to retry — closing the
        // editor + silently reverting would hide the failure.
        const ok = await onBeforeCommit(next);
        if (ok) {
          onChange(next);
          setEditing(false);
        } else {
          // User cancelled a confirm — close cleanly, no error to show.
          setEditing(false);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to save.');
      } finally {
        committingRef.current = false;
      }
      return;
    }
    onChange(next);
    setEditing(false);
  };

  if (!editing) {
    return (
      <div className="mt-1 flex items-center gap-1.5 text-[12px] text-slate-600">
        <span className="text-slate-400">Source:</span>
        <span className="font-medium text-slate-700 truncate">{value}</span>
        <button
          type="button"
          onClick={() => setEditing(true)}
          aria-label="Edit website"
          className="inline-flex items-center justify-center w-6 h-6 rounded-full text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
        >
          <Pencil className="w-3 h-3" />
        </button>
      </div>
    );
  }

  return (
    <div className="mt-1 space-y-1">
      <div className="flex items-center gap-2">
        <input
          type="text"
          autoFocus
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void save();
            } else if (e.key === 'Escape') {
              cancel();
            }
          }}
          className="flex-1 px-3 py-1.5 text-[12px] border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-offset-1"
          style={{ ['--tw-ring-color' as string]: accentColor }}
        />
        <PrimaryButton
          type="button"
          onClick={() => void save()}
          accentColor={accentColor}
          primaryColor={primaryColor}
          size="sm"
        >
          Save
        </PrimaryButton>
        <button
          type="button"
          onClick={cancel}
          className="text-[12px] font-medium text-slate-500 hover:text-slate-700 px-2 py-1.5 transition-colors"
        >
          Cancel
        </button>
      </div>
      {error && (
        <div className="text-[11px] font-medium text-rose-600">{error}</div>
      )}
    </div>
  );
};

export default UploadCreativeStep;
